// AppSidebar.js
// Permanent retrátil sidebar — replaces TopBar navigation.
// Desktop: icon-only (64px) or expanded (240px), permanent Drawer.
// Mobile: overlay Drawer, opens via hamburger in TopActionBar.
//
// Collapse state persisted in localStorage key 'progerar.sidebar.collapsed'.
// Default: mobile=closed, desktop >1280px=expanded, desktop narrow=icon-only.
//
// ATENÇÃO: lógica de logout/navegação copiada verbatim de TopBar.js para
// preservar comportamento exato. NÃO alterar lógica de negócio.

import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { withRouter } from '../../utils/withRouterCompat'
import { connect } from 'react-redux'
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  IconButton,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import BarChartIcon from '@mui/icons-material/BarChart'
import SchoolIcon from '@mui/icons-material/School'
import PeopleIcon from '@mui/icons-material/People'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ScienceIcon from '@mui/icons-material/Science'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate'
import LogoutIcon from '@mui/icons-material/Logout'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import MenuIcon from '@mui/icons-material/Menu'

import LogoZoetis from '../../assets/img/logo_corte.png'
import LogoZoetisSecondary from '../../assets/img/logozoetis_menu.png'
import { version } from '../../version'
import Prompt from '../Prompt'
import { logout, getLoggedUser } from '../../redux/auth/actions'
import { LocalStorageHelper } from '../../utils/localStorageHelper'
import clearCache from '../../utils/clearCache'
import clearStorage from '../../utils/clearStorage'
import hasInternet from '../../utils/recognizeInternetConnection'
import { updateNotes } from '../../utils/notesHelper'
import { utils } from '../../database'
import { recordEvent } from '../../observability/events'
import { syncQueue } from '../../sync/SyncQueue'

const SIDEBAR_WIDTH_EXPANDED = 240
const SIDEBAR_WIDTH_COLLAPSED = 64
const LS_KEY = 'progerar.sidebar.collapsed'

// Determina o estado inicial do collapse baseado no viewport.
function getInitialCollapsed(isWide) {
  try {
    const stored = localStorage.getItem(LS_KEY)
    if (stored !== null) return stored === 'true'
  } catch {
    // ignore
  }
  // Default: wide desktop = expanded (false), narrow = collapsed (true)
  return !isWide
}

function AppSidebar(props) {
  const {
    mobileOpen,
    onMobileClose,
    currentSync,
    loggedUser,
  } = props

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isWide = useMediaQuery('(min-width:1280px)')

  const [collapsed, setCollapsed] = useState(() => getInitialCollapsed(isWide))
  const [logoutModal, setLogoutModal] = useState(false)
  const [dlqModal, setDlqModal] = useState(false)
  const [semenModal, setSemenModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notesVisible, setNotesVisible] = useState(true)

  const navigate = useNavigate()
  const location = useLocation()

  // Persist collapse state
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, String(collapsed))
    } catch {
      // ignore
    }
  }, [collapsed])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => !c)
  }, [])

  const isActive = useCallback(
    (route) => {
      if (!route) return false
      if (route === '/') return location.pathname === '/'
      return location.pathname.startsWith(route)
    },
    [location.pathname]
  )

  const isOnIATFRoute =
    location.pathname.includes('IATF') ||
    location.pathname.includes('iatf')

  const canUsers =
    currentSync === 2 &&
    hasInternet() &&
    loggedUser?.roles?.[0] !== 'Cliente (somente visualização)'

  const isAdmin = loggedUser?.roles?.includes('Administrador')

  // ─── Nav sections ────────────────────────────────────────────────────────
  // Agrupamento lógico (2026-05-15): Dia a dia / Análise / Gestão.
  // "Meu Perfil" foi movido para bottomItems (convenção UX: conta + logout no rodapé).
  const navSections = [
    {
      key: 'daily',
      label: 'Dia a dia',
      items: [
        {
          key: 'home',
          label: 'Home',
          icon: <HomeIcon />,
          route: '/',
          show: true,
          disabled: false,
          onClick: () => {
            navigate('/')
            if (!isDesktop) onMobileClose()
          },
        },
        {
          key: 'protocols',
          label: 'Protocolos',
          icon: <AssignmentIcon />,
          route: '/protocols',
          show: true,
          disabled: false,
          onClick: () => {
            navigate('/protocols')
            if (!isDesktop) onMobileClose()
          },
        },
        {
          key: 'semen',
          label: 'Sêmen',
          icon: <ScienceIcon />,
          route: null,
          show: !isOnIATFRoute,
          disabled: false,
          onClick: () => {
            if (loggedUser?.isNewPartida) {
              navigate('/bulls/create-for-all/batch/none/management/none')
              if (!isDesktop) onMobileClose()
            } else {
              setSemenModal(true)
            }
          },
        },
      ],
    },
    {
      key: 'analysis',
      label: 'Análise',
      items: [
        {
          key: 'dashboard',
          label: 'Dashboard Geral',
          icon: <BarChartIcon />,
          route: '/dashboard-geral',
          show: true,
          disabled: false,
          onClick: () => {
            navigate('/dashboard-geral')
            if (!isDesktop) onMobileClose()
          },
        },
        {
          key: 'trainings',
          label: 'Treinamentos',
          icon: <SchoolIcon />,
          route: '/treinamentos',
          show: true,
          disabled: false,
          onClick: () => {
            navigate('/treinamentos')
            if (!isDesktop) onMobileClose()
          },
        },
      ],
    },
    {
      key: 'management',
      label: 'Gestão',
      items: [
        {
          key: 'users',
          label: 'Usuários',
          icon: <PeopleIcon />,
          route: '/users',
          show: true,
          disabled: !canUsers,
          onClick: () => {
            if (!canUsers) return
            navigate('/users')
            if (!isDesktop) onMobileClose()
          },
        },
        {
          key: 'admin',
          label: 'Admin',
          icon: <AdminPanelSettingsIcon />,
          route: '/lancamentos-do-administrador',
          show: !!isAdmin,
          disabled: false,
          onClick: () => {
            navigate('/lancamentos-do-administrador')
            if (!isDesktop) onMobileClose()
          },
        },
      ],
    },
  ]

  // ─── Bottom items ─────────────────────────────────────────────────────────
  const bottomItems = [
    {
      key: 'profile',
      label: 'Meu Perfil',
      icon: <AccountCircleIcon />,
      route: '/perfil',
      disabled: false,
      onClick: () => {
        navigate('/perfil')
        if (!isDesktop) onMobileClose()
      },
    },
    {
      key: 'update',
      label: 'Atualizar versão',
      icon: <SystemUpdateIcon />,
      disabled: !(currentSync === 2 && hasInternet()),
      onClick: () => {
        if (!(currentSync === 2 && hasInternet())) return
        setLoading(true)
        LocalStorageHelper.add('updateVersion', true)
        void recordEvent({
          level: 'info',
          category: 'outro',
          action: 'version_update_started',
          payload: { currentVersion: version },
        })
        clearCache()
      },
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: <LogoutIcon />,
      disabled: !(currentSync === 2 && hasInternet()),
      onClick: async () => {
        if (!(currentSync === 2 && hasInternet())) return
        recordEvent({ level: 'info', category: 'auth', action: 'logout' })
        try {
          const dlqCount = await syncQueue.getDeadLetterCount()
          if (dlqCount > 0) {
            setDlqModal(true)
          } else {
            setLogoutModal(true)
          }
        } catch {
          setLogoutModal(true)
        }
      },
    },
  ]

  // ─── Drawer content ───────────────────────────────────────────────────────
  const showCollapsed = collapsed && isDesktop
  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: showCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
        overflow: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      {/* ── Logo row ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: showCollapsed ? 1 : 2,
          py: 1.5,
          minHeight: 60,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {showCollapsed ? (
          <img
            src={LogoZoetisSecondary}
            alt="Zoetis"
            style={{ height: '36px', width: 'auto' }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img
              src={LogoZoetisSecondary}
              alt="Zoetis"
              style={{ height: '32px', width: 'auto' }}
            />
            <Box
              aria-hidden
              sx={{
                width: '1px',
                height: '24px',
                bgcolor: 'divider',
              }}
            />
            <img
              src={LogoZoetis}
              alt="ProGERAR"
              style={{ height: '28px', width: 'auto', cursor: 'pointer' }}
              onClick={() => {
                navigate('/')
                if (!isDesktop) onMobileClose()
              }}
            />
          </Box>
        )}
      </Box>

      {/* ── Toggle button (acima do primeiro nav item) ──
          Mesma linha pros dois estados — ícone troca conforme expandido/colapsado.
          Mobile não exibe (drawer fecha via overlay/swipe). */}
      {isDesktop && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: showCollapsed ? 'center' : 'flex-end',
            py: 0.5,
            px: showCollapsed ? 0 : 1,
          }}
        >
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            aria-label={showCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {showCollapsed ? (
              <MenuIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      )}

      {/* ── Nav sections ── */}
      <List sx={{ flex: 1, pt: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {navSections.map((section, sectionIdx) => {
          const visibleItems = section.items.filter((item) => item.show)
          if (visibleItems.length === 0) return null

          return (
            <React.Fragment key={section.key}>
              {showCollapsed
                ? sectionIdx > 0 && <Divider sx={{ my: 0.75, mx: 1.5 }} />
                : (
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      px: 2,
                      pt: sectionIdx === 0 ? 0.5 : 1.5,
                      pb: 0.25,
                      color: 'text.secondary',
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      lineHeight: 1.5,
                    }}
                  >
                    {section.label}
                  </Typography>
                )}
              {visibleItems.map((item) => {
                const active = isActive(item.route)
                const button = (
                  <ListItemButton
                    key={item.key}
                    onClick={item.onClick}
                    disabled={item.disabled}
                    selected={active}
                    sx={{
                      minHeight: 48,
                      justifyContent: showCollapsed ? 'center' : 'initial',
                      px: showCollapsed ? 0 : 2,
                      borderRadius: 1,
                      mx: 0.5,
                      mb: 0.25,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '& .MuiListItemIcon-root': { color: 'white' },
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: showCollapsed ? 0 : 2,
                        justifyContent: 'center',
                        color: item.disabled
                          ? 'text.disabled'
                          : active
                          ? 'inherit'
                          : 'text.secondary',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!showCollapsed && (
                      <ListItemText
                        primary={item.label}
                        slotProps={{
                          primary: {
                            fontSize: '0.9rem',
                            fontWeight: active ? 600 : 400,
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                )

                if (showCollapsed) {
                  return (
                    <Tooltip key={item.key} title={item.label} placement="right" arrow>
                      <span>{button}</span>
                    </Tooltip>
                  )
                }
                return button
              })}
            </React.Fragment>
          )
        })}
      </List>

      <Divider />

      {/* ── Bottom items ── */}
      <List sx={{ pb: 1 }}>
        {bottomItems.map((item) => {
          const active = item.route ? isActive(item.route) : false
          const button = (
            <ListItemButton
              key={item.key}
              onClick={item.onClick}
              disabled={item.disabled}
              selected={active}
              sx={{
                minHeight: 48,
                justifyContent: showCollapsed ? 'center' : 'initial',
                px: showCollapsed ? 0 : 2,
                borderRadius: 1,
                mx: 0.5,
                mb: 0.25,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': { color: 'white' },
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: showCollapsed ? 0 : 2,
                  justifyContent: 'center',
                  color: item.disabled
                    ? 'text.disabled'
                    : active
                    ? 'inherit'
                    : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!showCollapsed && (
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      fontSize: '0.9rem',
                      fontWeight: active ? 600 : 400,
                    },
                  }}
                />
              )}
            </ListItemButton>
          )

          if (showCollapsed) {
            return (
              <Tooltip key={item.key} title={item.label} placement="right" arrow>
                <span>{button}</span>
              </Tooltip>
            )
          }
          return button
        })}
      </List>

      {/* ── App version (último elemento, abaixo do Sair) ── */}
      {!showCollapsed && (
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            pt: 0.5,
            textAlign: 'center',
            color: 'text.disabled',
            fontSize: '0.7rem',
            letterSpacing: '0.03em',
            userSelect: 'none',
            lineHeight: 1.4,
          }}
        >
          {`Versão: ${version}`}
        </Box>
      )}
    </Box>
  )

  // ─── Prompts (copiados de TopBar.js) ──────────────────────────────────────

  const dlqPrompt = (
    <Prompt
      visible={dlqModal}
      title="Sair sem sincronizar?"
      message="Alguns registros aguardam envio ao servidor. Se você sair agora, eles serão perdidos. Deseja continuar mesmo assim?"
      buttons={[
        {
          label: 'Voltar',
          onClick: () => setDlqModal(false),
        },
        {
          label: 'Sair mesmo assim',
          onClick: () => {
            setDlqModal(false)
            setLogoutModal(true)
          },
        },
      ]}
    />
  )

  const logoutPrompt = (
    <Prompt
      visible={logoutModal}
      title="Atenção!"
      message={`Antes de sair certifique-se que seus dados foram sincronizados. Caso você esteja offline, clique em cancelar e assim que possível conecte-se a rede para enviar os dados para o servidor.`}
      buttons={[
        {
          label: 'Cancelar',
          onClick: () => setLogoutModal(false),
        },
        {
          label: 'Sair',
          onClick: async () => {
            const res = await getLoggedUser()
            localStorage.setItem(
              `S - ${new Date()}`,
              JSON.stringify({ userId: res.userId })
            )
            const result = await clearStorage(currentSync)
            if (!result.success) {
              if (result.reason === 'OFFLINE_WITH_PENDING_DATA') {
                alert(`Não é possível sair: você está offline e possui ${result.pendingCount || 'alguns'} registros pendentes de sincronização. Conecte-se à internet primeiro.`)
                setLogoutModal(false)
                return
              } else if (result.reason === 'SYNC_INCOMPLETE') {
                alert(`Ainda existem ${result.pendingCount} registros pendentes. Aguarde a sincronização completar.`)
                setLogoutModal(false)
                return
              } else if (result.reason === 'SYNC_FAILED') {
                alert('Erro ao sincronizar dados. Verifique sua conexão e tente novamente.')
                setLogoutModal(false)
                return
              }
              console.error('[AppSidebar] Erro desconhecido no logout:', result)
              alert('Erro ao sair. Tente novamente.')
              setLogoutModal(false)
              return
            }
            localStorage.removeItem('downloadFarms')
            LocalStorageHelper.remove('selectedEstacaoDeMonta')
            props.logout()
            window.location.href = '/login'
          },
        },
      ]}
    />
  )

  const semenPrompt = (
    <Prompt
      visible={semenModal && !loggedUser?.isNewPartida}
      title="Atenção!"
      message="Tivemos alterações importantes na estrutura dos dados de Touro e Partida. Para evitar problemas na sincronização dos seus dados é necessário retornar a tela de login e entrar com seu usuário e senha novamente. Caso tenha dúvidas ou problemas, entre em contato com nossa equipe de atendimento, estaremos à disposição."
      buttons={[
        {
          label: 'Fechar',
          onClick: () => setSemenModal(false),
        },
        {
          label: 'Ir para o Login',
          disabled: !(currentSync === 2 && hasInternet()),
          onClick: async () => {
            const res = await getLoggedUser()
            localStorage.setItem(
              `S - ${new Date()}`,
              JSON.stringify({ userId: res.userId })
            )
            const result = await clearStorage(currentSync)
            if (!result.success) {
              if (result.reason === 'OFFLINE_WITH_PENDING_DATA') {
                alert(`Não é possível sair: você está offline e possui ${result.pendingCount || 'alguns'} registros pendentes de sincronização. Conecte-se à internet primeiro.`)
                setSemenModal(false)
                return
              } else if (result.reason === 'SYNC_INCOMPLETE') {
                alert(`Ainda existem ${result.pendingCount} registros pendentes. Aguarde a sincronização completar.`)
                setSemenModal(false)
                return
              } else if (result.reason === 'SYNC_FAILED') {
                alert('Erro ao sincronizar dados. Verifique sua conexão e tente novamente.')
                setSemenModal(false)
                return
              }
              console.error('[AppSidebar] Erro desconhecido (semen):', result)
              alert('Erro ao sair. Tente novamente.')
              setSemenModal(false)
              return
            }
            localStorage.removeItem('downloadFarms')
            LocalStorageHelper.remove('selectedEstacaoDeMonta')
            props.logout()
            window.location.href = '/login'
          },
        },
      ]}
    />
  )

  const versionPrompt = (
    <Prompt
      visible={!!LocalStorageHelper.get('updateVersion') && notesVisible && !loading}
      title={updateNotes.title}
      message={
        <>
          <p style={{ margin: '0 0 12px 0' }}>
            Você está agora na versão <strong>{version}</strong>.
          </p>
          <p style={{ margin: 0 }}>
            O cache do navegador foi limpo e a aplicação foi recarregada com a versão mais recente disponível no servidor.
          </p>
        </>
      }
      buttons={[
        {
          label: 'Ok',
          onClick: () => {
            setNotesVisible(false)
            LocalStorageHelper.remove('updateVersion')
          },
        },
      ]}
    />
  )

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* Desktop: permanent drawer */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          open
          sx={{
            width: showCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: showCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
              boxSizing: 'border-box',
              overflow: 'hidden',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
            '@media print': { display: 'none' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile: temporary overlay drawer */}
      {!isDesktop && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH_EXPANDED,
              boxSizing: 'border-box',
            },
            '@media print': { display: 'none' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Loading overlay (atualizar versão) */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      )}

      {dlqPrompt}
      {logoutPrompt}
      {semenPrompt}
      {versionPrompt}
    </>
  )
}

const mapStateToProps = (state) => ({
  currentSync: state.sync.currentSync,
})

const mapDispatchToProps = (dispatch) => ({
  logout: async () => await dispatch(logout()),
})

const propsFromDatabase = async () => {
  const loggedUser = await getLoggedUser()
  return { loggedUser }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(utils.withPropsFromDatabase(propsFromDatabase, withRouter(AppSidebar)))
